import { supabase } from './client';
import { getActivitySlideTypes } from '../slides/slideRegistry';

// ============================================================
// TYPES
// ============================================================

export type TeacherSettings = {
  id: string;
  teacher_id: string;
  grading_weight_quiz: number;
  grading_weight_participation: number;
  allow_late_submission: boolean;
  late_penalty_percent: number;
  default_mode: 'live' | 'student-paced';
  auto_grade_enabled: boolean;
  created_at: string;
  updated_at: string;
};

export type GradeSummary = {
  id: string;
  student_name: string;
  session_id: string;
  lesson_id: string;
  teacher_id: string;
  total_score: number;
  max_possible_score: number;
  percentage: number;
  completed_activities: number;
  total_activities: number;
  completion_rate: number;
  participation_rate: number;
  time_spent_minutes: number;
  last_activity_at: string | null;
  created_at: string;
  updated_at: string;
};

export type SessionAnalytics = {
  id: string;
  session_id: string;
  session_code: string;
  lesson_id: string;
  teacher_id: string;
  lesson_title: string;
  mode: string;
  status: string;
  total_participants: number;
  avg_score: number;
  avg_completion_rate: number;
  avg_participation_rate: number;
  total_submissions: number;
  total_security_incidents: number;
  tab_switch_incidents: number;
  screenshot_incidents: number;
  app_switch_incidents: number;
  last_security_incident_at: string | null;
  session_duration_minutes: number;
  started_at: string | null;
  ended_at: string | null;
  created_at: string;
  updated_at: string;
};

export type SessionSecurityIncident = {
  id: string;
  session_id: string;
  slide_id: string | null;
  student_name: string;
  incident_type: 'tab-switch' | 'screenshot' | 'app-switch';
  metadata: {
    slideIndex?: number;
    path?: string;
    userAgent?: string;
    visibilityState?: string;
    timestamp?: string;
    [key: string]: any;
  } | null;
  created_at: string;
  slides?: {
    type: string;
    slide_order: number;
  } | null;
};

export type LessonSummaryReport = {
  lesson_id: string;
  teacher_id: string;
  lesson_title: string;
  mode: 'live' | 'student-paced';
  total_sessions: number;
  avg_score: number;
  avg_completion_rate: number;
  avg_participation_rate: number;
  total_students: number;
  total_submissions: number;
  last_session_date: string | null;
};

export type StudentPerformanceReport = {
  student_name: string;
  teacher_id: string;
  sessions_attended: number;
  avg_percentage: number;
  avg_completion_rate: number;
  avg_participation_rate: number;
  total_score: number;
  last_active: string | null;
  passing_sessions: number;
  total_graded_sessions: number;
};

export type ReportFilters = {
  classroom_id?: string;
  lesson_id?: string;
  mode?: 'live' | 'student-paced';
  date_from?: string;
  date_to?: string;
};

export type PaginationParams = {
  page: number;
  pageSize: number;
};

// ============================================================
// TEACHER SETTINGS
// ============================================================

export async function getTeacherSettings(teacherId: string): Promise<TeacherSettings | null> {
  console.log('[Analytics] Fetching teacher settings:', teacherId);
  
  const { data, error } = await supabase
    .from('teacher_settings')
    .select('id, teacher_id, grading_weight_quiz, grading_weight_participation, allow_late_submission, late_penalty_percent, default_mode, auto_grade_enabled, created_at, updated_at')
    .eq('teacher_id', teacherId)
    .single();

  if (error) {
    console.error('[Analytics] Error fetching teacher settings:', error);
    // If no settings exist, create default
    if (error.code === 'PGRST116') {
      return createDefaultTeacherSettings(teacherId);
    }
    throw error;
  }

  console.log('[Analytics] Teacher settings loaded:', data);
  return data;
}

async function createDefaultTeacherSettings(teacherId: string): Promise<TeacherSettings> {
  console.log('[Analytics] Creating default teacher settings');
  
  const { data, error } = await supabase
    .from('teacher_settings')
    .insert({
      teacher_id: teacherId,
      grading_weight_quiz: 70,
      grading_weight_participation: 30,
      allow_late_submission: true,
      late_penalty_percent: 10,
      default_mode: 'live',
      auto_grade_enabled: true
    })
    .select()
    .single();

  if (error) {
    console.error('[Analytics] Error creating teacher settings:', error);
    throw error;
  }

  return data;
}

export async function updateTeacherSettings(
  teacherId: string,
  settings: Partial<TeacherSettings>
): Promise<TeacherSettings> {
  console.log('[Analytics] Updating teacher settings:', settings);
  
  const { data, error } = await supabase
    .from('teacher_settings')
    .update({
      ...settings,
      updated_at: new Date().toISOString()
    })
    .eq('teacher_id', teacherId)
    .select()
    .single();

  if (error) {
    console.error('[Analytics] Error updating teacher settings:', error);
    throw error;
  }

  console.log('[Analytics] Teacher settings updated');
  return data;
}

// ============================================================
// REPORTS
// ============================================================

export async function getLessonSummaryReports(
  teacherId: string,
  filters?: ReportFilters,
  pagination?: PaginationParams
): Promise<{ data: LessonSummaryReport[]; total: number }> {
  console.log('[Analytics] Fetching lesson summary reports:', { filters, pagination });
  
  // Get lesson IDs - both owned and shared
  const { data: ownedLessons, error: ownedError } = await supabase
    .from('lessons')
    .select('id')
    .eq('teacher_id', teacherId);

  if (ownedError) {
    console.error('[Analytics] Error fetching owned lessons:', ownedError);
    throw ownedError;
  }

  const { data: sharedLessons, error: sharedError } = await supabase
    .from('lesson_shares')
    .select('lesson_id')
    .eq('shared_to_teacher_id', teacherId);

  if (sharedError) {
    console.error('[Analytics] Error fetching shared lessons:', sharedError);
    throw sharedError;
  }

  const ownedLessonIds = (ownedLessons || []).map(l => l.id);
  const sharedLessonIds = (sharedLessons || []).map(l => l.lesson_id);
  const allLessonIds = [...ownedLessonIds, ...sharedLessonIds];

  if (allLessonIds.length === 0) {
    return { data: [], total: 0 };
  }

  let query = supabase
    .from('lesson_summary_report')
    .select('lesson_id, teacher_id, lesson_title, mode, total_sessions, avg_score, avg_completion_rate, avg_participation_rate, total_students, total_submissions, last_session_date', { count: 'exact' })
    .in('lesson_id', allLessonIds)
    .order('last_session_date', { ascending: false, nullsFirst: false });

  // Apply filters
  if (filters?.mode) {
    query = query.eq('mode', filters.mode);
  }

  if (filters?.lesson_id) {
    query = query.eq('lesson_id', filters.lesson_id);
  }

  // Apply pagination
  if (pagination) {
    const { page, pageSize } = pagination;
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    query = query.range(from, to);
  }

  const { data, error, count } = await query;

  if (error) {
    console.error('[Analytics] Error fetching lesson reports:', error);
    throw error;
  }

  console.log(`[Analytics] Loaded ${data?.length || 0} lesson reports`);
  return { data: data || [], total: count || 0 };
}

export async function getStudentPerformanceReports(
  teacherId: string,
  searchQuery?: string,
  pagination?: PaginationParams
): Promise<{ data: StudentPerformanceReport[]; total: number }> {
  console.log('[Analytics] Fetching student performance reports:', { searchQuery, pagination });
  
  // Get session IDs from owned and shared lessons
  const { data: ownedSessions, error: ownedError } = await supabase
    .from('sessions')
    .select('id')
    .eq('teacher_id', teacherId);

  if (ownedError) {
    console.error('[Analytics] Error fetching owned sessions:', ownedError);
    throw ownedError;
  }

  const { data: sharedLessons, error: sharedError } = await supabase
    .from('lesson_shares')
    .select('lesson_id')
    .eq('shared_to_teacher_id', teacherId);

  if (sharedError) {
    console.error('[Analytics] Error fetching shared lessons:', sharedError);
    throw sharedError;
  }

  const sharedLessonIds = (sharedLessons || []).map(l => l.lesson_id);
  let sharedSessionIds: string[] = [];

  if (sharedLessonIds.length > 0) {
    const { data: sharedSessionsData, error: sharedSessionsError } = await supabase
      .from('sessions')
      .select('id')
      .in('lesson_id', sharedLessonIds);

    if (sharedSessionsError) {
      console.error('[Analytics] Error fetching shared sessions:', sharedSessionsError);
      throw sharedSessionsError;
    }
    sharedSessionIds = (sharedSessionsData || []).map(s => s.id);
  }

  const ownedSessionIds = (ownedSessions || []).map(s => s.id);
  const allSessionIds = [...ownedSessionIds, ...sharedSessionIds];

  if (allSessionIds.length === 0) {
    return { data: [], total: 0 };
  }

  // Fetch student performance data from grade_summary for these sessions
  let query = supabase
    .from('grade_summary')
    .select('student_name, session_id', { count: 'exact' })
    .in('session_id', allSessionIds);

  // Search by student name
  if (searchQuery) {
    query = query.ilike('student_name', `%${searchQuery}%`);
  }

  const { data: gradeData, error: gradeError } = await query;

  if (gradeError) {
    console.error('[Analytics] Error fetching grade summary:', gradeError);
    throw gradeError;
  }

  // Aggregate student performance across all their sessions
  const studentMap = new Map<string, StudentPerformanceReport>();
  
  for (const grade of (gradeData || [])) {
    const studentName = grade.student_name;
    if (!studentMap.has(studentName)) {
      // Fetch detailed stats for this student
      const { data: studentGrades, error: studentError } = await supabase
        .from('grade_summary')
        .select('total_score, max_possible_score, percentage, completion_rate, participation_rate, last_activity_at')
        .eq('student_name', studentName)
        .in('session_id', allSessionIds);

      if (studentError) continue;

      const sessions = studentGrades || [];
      const sessionCount = sessions.length;
      const avgPercentage = sessions.reduce((sum, s) => sum + (s.percentage || 0), 0) / (sessionCount || 1);
      const avgCompletion = sessions.reduce((sum, s) => sum + (s.completion_rate || 0), 0) / (sessionCount || 1);
      const avgParticipation = sessions.reduce((sum, s) => sum + (s.participation_rate || 0), 0) / (sessionCount || 1);
      const totalScore = sessions.reduce((sum, s) => sum + (s.total_score || 0), 0);
      const lastActive = sessions.reduce((latest, s) => {
        const current = new Date(s.last_activity_at || 0);
        return current > latest ? current : latest;
      }, new Date(0)).toISOString();
      const passingCount = sessions.filter(s => (s.percentage || 0) >= 60).length;

      studentMap.set(studentName, {
        student_name: studentName,
        teacher_id: teacherId,
        sessions_attended: sessionCount,
        avg_percentage: avgPercentage,
        avg_completion_rate: avgCompletion,
        avg_participation_rate: avgParticipation,
        total_score: totalScore,
        last_active: lastActive,
        passing_sessions: passingCount,
        total_graded_sessions: sessionCount
      });
    }
  }

  let studentReports = Array.from(studentMap.values())
    .sort((a, b) => b.avg_percentage - a.avg_percentage);

  // Apply pagination
  const total = studentReports.length;
  if (pagination) {
    const { page, pageSize } = pagination;
    const from = (page - 1) * pageSize;
    const to = from + pageSize;
    studentReports = studentReports.slice(from, to);
  }

  console.log(`[Analytics] Loaded ${studentReports.length} student performance reports`);
  return { data: studentReports, total };
}

export async function getSessionAnalytics(
  teacherId: string,
  filters?: ReportFilters,
  pagination?: PaginationParams
): Promise<{ data: SessionAnalytics[]; total: number }> {
  console.log('[Analytics] Fetching session analytics:', { filters, pagination });
  
  // Get lesson IDs - both owned and shared
  const { data: ownedLessons, error: ownedError } = await supabase
    .from('lessons')
    .select('id')
    .eq('teacher_id', teacherId);

  if (ownedError) {
    console.error('[Analytics] Error fetching owned lessons:', ownedError);
    throw ownedError;
  }

  const { data: sharedLessons, error: sharedError } = await supabase
    .from('lesson_shares')
    .select('lesson_id')
    .eq('shared_to_teacher_id', teacherId);

  if (sharedError) {
    console.error('[Analytics] Error fetching shared lessons:', sharedError);
    throw sharedError;
  }

  const ownedLessonIds = (ownedLessons || []).map(l => l.id);
  const sharedLessonIds = (sharedLessons || []).map(l => l.lesson_id);
  const allLessonIds = [...ownedLessonIds, ...sharedLessonIds];

  if (allLessonIds.length === 0) {
    return { data: [], total: 0 };
  }

  let query = supabase
    .from('session_analytics')
    .select('id, session_id, session_code, lesson_id, teacher_id, lesson_title, mode, status, total_participants, avg_score, avg_completion_rate, avg_participation_rate, total_submissions, total_security_incidents, tab_switch_incidents, screenshot_incidents, app_switch_incidents, last_security_incident_at, session_duration_minutes, started_at, ended_at, created_at, updated_at', { count: 'exact' })
    .in('lesson_id', allLessonIds)
    .order('ended_at', { ascending: false, nullsFirst: false });

  // Apply filters
  if (filters?.lesson_id) {
    query = query.eq('lesson_id', filters.lesson_id);
  }

  if (filters?.date_from) {
    query = query.gte('ended_at', filters.date_from);
  }

  if (filters?.date_to) {
    query = query.lte('ended_at', filters.date_to);
  }

  // Apply pagination
  if (pagination) {
    const { page, pageSize } = pagination;
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    query = query.range(from, to);
  }

  const { data, error, count } = await query;

  if (error) {
    console.error('[Analytics] Error fetching session analytics:', error);
    throw error;
  }

  console.log(`[Analytics] Loaded ${data?.length || 0} session analytics`);
  return { data: data || [], total: count || 0 };
}

export async function getSessionSecurityIncidents(sessionId: string): Promise<SessionSecurityIncident[]> {
  console.log('[Analytics] Fetching session security incidents:', { sessionId });

  const { data, error } = await supabase
    .from('session_security_incidents')
    .select('id, session_id, slide_id, student_name, incident_type, metadata, created_at, slides(type, slide_order)')
    .eq('session_id', sessionId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[Analytics] Error fetching session security incidents:', error);
    throw error;
  }

  return (data || []) as SessionSecurityIncident[];
}

// ============================================================
// STUDENTS & GRADES
// ============================================================

export async function getSessionStudents(
  sessionId: string,
  searchQuery?: string,
  sortBy: 'name' | 'score' | 'completion' = 'name',
  pagination?: PaginationParams
): Promise<{ data: GradeSummary[]; total: number }> {
  console.log('[Analytics] Fetching session students:', { sessionId, searchQuery, sortBy, pagination });
  
  // Try grade_summary first (preferred for full analytics data)
  let query = supabase
    .from('grade_summary')
    .select('id, student_name, session_id, lesson_id, teacher_id, total_score, max_possible_score, percentage, completed_activities, total_activities, completion_rate, participation_rate, time_spent_minutes, last_activity_at, created_at, updated_at', { count: 'exact' })
    .eq('session_id', sessionId);

  // Search
  if (searchQuery) {
    query = query.ilike('student_name', `%${searchQuery}%`);
  }

  // Sort
  switch (sortBy) {
    case 'score':
      query = query.order('percentage', { ascending: false });
      break;
    case 'completion':
      query = query.order('completion_rate', { ascending: false });
      break;
    default:
      query = query.order('student_name', { ascending: true });
  }

  // Pagination
  if (pagination) {
    const { page, pageSize } = pagination;
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    query = query.range(from, to);
  }

  let { data, error, count } = await query;

  // Fallback: if grade_summary doesn't exist or is empty, use session_participants
  if (error || !data || data.length === 0) {
    console.log('[Analytics] Falling back to session_participants query');
    
    let fallbackQuery = supabase
      .from('session_participants')
      .select('id, student_name, session_id, joined_at, progress_percentage', { count: 'exact' })
      .eq('session_id', sessionId);

    // Search
    if (searchQuery) {
      fallbackQuery = fallbackQuery.ilike('student_name', `%${searchQuery}%`);
    }

    // Sort
    switch (sortBy) {
      case 'score':
      case 'completion':
        fallbackQuery = fallbackQuery.order('progress_percentage', { ascending: false });
        break;
      default:
        fallbackQuery = fallbackQuery.order('student_name', { ascending: true });
    }

    // Pagination
    if (pagination) {
      const { page, pageSize } = pagination;
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      fallbackQuery = fallbackQuery.range(from, to);
    }

    const fallbackResult = await fallbackQuery;
    if (fallbackResult.error) {
      console.error('[Analytics] Error fetching session students (both queries failed):', fallbackResult.error);
      throw fallbackResult.error;
    }

    // Map session_participants to GradeSummary format
    data = fallbackResult.data?.map(participant => ({
      id: participant.id,
      student_name: participant.student_name,
      session_id: participant.session_id,
      lesson_id: '', // Not available from session_participants
      teacher_id: '', // Not available from session_participants
      total_score: 0,
      max_possible_score: 0,
      percentage: 0,
      completed_activities: 0,
      total_activities: 0,
      completion_rate: participant.progress_percentage || 0,
      participation_rate: 0,
      time_spent_minutes: 0,
      last_activity_at: null,
      created_at: participant.joined_at || new Date().toISOString(),
      updated_at: new Date().toISOString()
    })) || [];
    count = fallbackResult.count || 0;
  }

  console.log(`[Analytics] Loaded ${data?.length || 0} students`);
  return { data: data || [], total: count || 0 };
}

export async function getStudentGrades(
  sessionId: string,
  studentName: string
): Promise<any[]> {
  console.log('[Analytics] Fetching student grades:', { sessionId, studentName });
  
  const { data, error } = await supabase
    .from('grades')
    .select('id, slide_id, student_name, score, max_score, is_correct, auto_graded, created_at')
    .eq('session_id', sessionId)
    .eq('student_name', studentName)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('[Analytics] Error fetching student grades:', error);
    throw error;
  }

  return data || [];
}

export async function updateGrade(
  gradeId: string,
  score: number,
  maxScore: number
): Promise<void> {
  console.log('[Analytics] Updating grade:', { gradeId, score, maxScore });
  
  const isCorrect = score >= maxScore * 0.7; // 70% threshold
  
  const { error } = await supabase
    .from('grades')
    .update({
      score,
      max_score: maxScore,
      is_correct: isCorrect,
      auto_graded: false
    })
    .eq('id', gradeId);

  if (error) {
    console.error('[Analytics] Error updating grade:', error);
    throw error;
  }

  console.log('[Analytics] Grade updated successfully');
}

export async function removeStudentFromSession(
  sessionId: string,
  studentName: string
): Promise<void> {
  console.log('[Analytics] Removing student from session:', { sessionId, studentName });
  
  // Delete from submissions first (dependent table)
  const { error: submissionsError } = await supabase
    .from('submissions')
    .delete()
    .eq('session_id', sessionId)
    .eq('student_name', studentName);

  if (submissionsError) {
    console.error('[Analytics] Error removing submissions:', submissionsError);
    throw submissionsError;
  }

  // Delete from grades
  const { error: gradesError } = await supabase
    .from('grades')
    .delete()
    .eq('session_id', sessionId)
    .eq('student_name', studentName);

  if (gradesError) {
    console.error('[Analytics] Error removing grades:', gradesError);
    throw gradesError;
  }

  // Delete from grade_summary
  const { error: summaryError } = await supabase
    .from('grade_summary')
    .delete()
    .eq('session_id', sessionId)
    .eq('student_name', studentName);

  if (summaryError) {
    console.error('[Analytics] Error removing grade summary:', summaryError);
    throw summaryError;
  }

  // Delete from participants (source table)
  const { error: participantError } = await supabase
    .from('session_participants')
    .delete()
    .eq('session_id', sessionId)
    .eq('student_name', studentName);

  if (participantError) {
    console.error('[Analytics] Error removing participant:', participantError);
    throw participantError;
  }

  console.log('[Analytics] Student removed successfully');
}

// ============================================================
// GRADEBOOK
// ============================================================

export async function getLessonGradebook(
  lessonId: string,
  pagination?: PaginationParams
): Promise<{
  students: GradeSummary[];
  activities: { id: string; title: string; type: string; order: number }[];
  total: number;
}> {
  console.log('[Analytics] Fetching lesson gradebook:', { lessonId, pagination });
  
  // Get the complete list of activity types from slideRegistry
  const activityTypes = getActivitySlideTypes().map((activity) => activity.type);
  
  // Fetch activities (slides that can be graded)
  const { data: activities, error: activitiesError } = await supabase
    .from('slides')
    .select('id, content, type, slide_order')
    .eq('lesson_id', lessonId)
    .in('type', activityTypes)
    .order('slide_order', { ascending: true });

  if (activitiesError) {
    console.error('[Analytics] Error fetching activities:', activitiesError);
    throw activitiesError;
  }

  // Fetch student grade summaries
  let query = supabase
    .from('grade_summary')
    .select('id, student_name, session_id, lesson_id, teacher_id, total_score, max_possible_score, percentage, completed_activities, total_activities, completion_rate, participation_rate, time_spent_minutes, last_activity_at, created_at, updated_at', { count: 'exact' })
    .eq('lesson_id', lessonId)
    .order('student_name', { ascending: true });

  if (pagination) {
    const { page, pageSize } = pagination;
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    query = query.range(from, to);
  }

  const { data: students, error: studentsError, count } = await query;

  if (studentsError) {
    console.error('[Analytics] Error fetching students:', studentsError);
    throw studentsError;
  }

  console.log(`[Analytics] Loaded gradebook: ${students?.length || 0} students, ${activities?.length || 0} activities`);
  
  return {
    students: students || [],
    activities: (activities || []).map(a => ({
      id: a.id,
      title: a.content?.title || 'Untitled',
      type: a.type,
      order: a.slide_order
    })),
    total: count || 0
  };
}

export async function getActivityGrades(
  sessionId: string,
  slideId: string
): Promise<any[]> {
  console.log('[Analytics] Fetching activity grades:', { sessionId, slideId });
  
  const { data, error } = await supabase
    .from('grades')
    .select('id, student_name, score, max_score, is_correct, auto_graded, created_at')
    .eq('session_id', sessionId)
    .eq('slide_id', slideId)
    .order('student_name', { ascending: true });

  if (error) {
    console.error('[Analytics] Error fetching activity grades:', error);
    throw error;
  }

  return data || [];
}

export async function getStudentActivityGrades(
  sessionId: string,
  studentName: string
): Promise<any[]> {
  console.log('[Analytics] Fetching student activity grades:', { sessionId, studentName });
  
  const { data, error } = await supabase
    .from('submissions')
    .select(`
      id,
      slide_id,
      student_name,
      answer,
      score,
      graded,
      created_at,
      slides (
        id,
        type,
        content
      )
    `)
    .eq('session_id', sessionId)
    .eq('student_name', studentName)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('[Analytics] Error fetching student activity grades:', error);
    throw error;
  }

  return data || [];
}

export async function getAllStudentActivityGrades(
  sessionId: string,
  studentName: string
): Promise<any[]> {
  console.log('[Analytics] Fetching all student activity grades (with unsubmitted):', { sessionId, studentName });
  
  try {
    // Get session details to find lesson_id
    const { data: session, error: sessionError } = await supabase
      .from('sessions')
      .select('lesson_id')
      .eq('id', sessionId)
      .single();

    if (sessionError || !session) {
      throw new Error('Session not found');
    }

    // Get all slides from the lesson
    const { data: allSlides, error: slidesError } = await supabase
      .from('slides')
      .select('id, lesson_id, type, content, slide_order, created_at')
      .eq('lesson_id', session.lesson_id)
      .order('slide_order', { ascending: true });

    if (slidesError) throw slidesError;

    // Filter to only gradable activities - must match grade-calculator.ts
    const gradableTypes = ['multiple_choice', 'true_false', 'poll', 'open_ended', 
                           'matching_pairs', 'time_to_climb', 'draw_it', 'fill_blanks', 
                           'quiz', 'collaborate_board', 'word_cloud', 'drag_drop', 'sequencing'];
    const gradableSlides = (allSlides || []).filter(slide => gradableTypes.includes(slide.type));

    // Get submissions for this student
    const { data: submissions, error: submissionsError } = await supabase
      .from('submissions')
      .select(`
        id,
        slide_id,
        student_name,
        answer,
        score,
        graded,
        created_at,
        slides (
          id,
          type,
          content
        )
      `)
      .eq('session_id', sessionId)
      .eq('student_name', studentName)
      .order('created_at', { ascending: true });

    if (submissionsError) throw submissionsError;

    // Create a map of submissions by slide_id
    const submissionMap = new Map(
      (submissions || []).map(sub => [sub.slide_id, sub])
    );

    // Combine all slides with submissions
    const allActivities = gradableSlides.map(slide => {
      const submission = submissionMap.get(slide.id);
      
      if (submission) {
        // Return the submission as-is
        return submission;
      } else {
        // Create a placeholder for unsubmitted activity
        return {
          id: null,
          slide_id: slide.id,
          student_name: studentName,
          answer: null,
          score: null,
          graded: false,
          created_at: null,
          slides: {
            id: slide.id,
            type: slide.type,
            content: slide.content
          }
        };
      }
    });

    return allActivities;
  } catch (error) {
    console.error('[Analytics] Error fetching all student activity grades:', error);
    throw error;
  }
}

export async function updateSubmissionGrade(
  submissionId: string,
  score: number,
  feedback?: string
): Promise<void> {
  console.log('[Analytics] Updating submission grade:', { submissionId, score, feedback });
  
  // First get the current submission to merge feedback and get session/student info
  const { data: currentSubmission, error: fetchError } = await supabase
    .from('submissions')
    .select('answer, session_id, student_name')
    .eq('id', submissionId)
    .single();

  if (fetchError) {
    console.error('[Analytics] Error fetching submission:', fetchError);
    throw fetchError;
  }

  // Merge feedback into answer object
  const updatedAnswer = {
    ...(currentSubmission?.answer || {}),
    feedback: feedback || currentSubmission?.answer?.feedback
  };

  const { error } = await supabase
    .from('submissions')
    .update({
      score,
      graded: true,
      answer: updatedAnswer,
      updated_at: new Date().toISOString()
    })
    .eq('id', submissionId);

  if (error) {
    console.error('[Analytics] Error updating submission grade:', error);
    throw error;
  }

  // Recalculate student's total grade
  if (currentSubmission) {
    await recalculateStudentGrade(currentSubmission.session_id, currentSubmission.student_name);
  }

  console.log('[Analytics] Submission grade updated successfully');
}

/**
 * Recalculate and update a student's total grade based on all their submissions
 */
export async function recalculateStudentGrade(
  sessionId: string,
  studentName: string
): Promise<void> {
  console.log('[Analytics] Recalculating student grade:', { sessionId, studentName });

  // Get session and lesson info
  const { data: session, error: sessionError } = await supabase
    .from('sessions')
    .select('lesson_id')
    .eq('id', sessionId)
    .single();

  if (sessionError) {
    console.error('[Analytics] Error fetching session:', sessionError);
    throw sessionError;
  }

  // Get all submissions for this student in this session
  const { data: submissions, error: submissionsError } = await supabase
    .from('submissions')
    .select(`
      id,
      score,
      graded,
      slide_id,
      slides (
        type,
        content
      )
    `)
    .eq('session_id', sessionId)
    .eq('student_name', studentName);

  if (submissionsError) {
    console.error('[Analytics] Error fetching submissions:', submissionsError);
    throw submissionsError;
  }

  // Calculate totals
  let totalScore = 0;
  let maxPossibleScore = 0;
  let completedActivities = 0;
  let totalActivities = 0;
  
  // Use the complete list of activity types from slideRegistry
  const activityTypes = getActivitySlideTypes().map((activity) => activity.type);

  for (const submission of submissions) {
    const slide = (submission as any).slides;
    if (!slide) continue;

    // Check if this is a gradable activity
    const isGradable = activityTypes.includes(slide.type);
    
    if (isGradable) {
      totalActivities++;
      const maxPoints = slide.content?.points || slide.content?.maxPoints || (slide.type === 'quiz' ? 100 : slide.type === 'time_to_climb' ? 50 : 10);
      maxPossibleScore += maxPoints;

      if (submission.graded && submission.score !== null) {
        // Cap score to never exceed the slide's max points
        totalScore += Math.min(submission.score, maxPoints);
        completedActivities++;
      }
    }
  }

  const percentage = maxPossibleScore > 0 ? (totalScore / maxPossibleScore) * 100 : 0;
  const completionRate = totalActivities > 0 ? (completedActivities / totalActivities) * 100 : 0;

  // Update grades table
  const { error: gradesError } = await supabase
    .from('grades')
    .upsert({
      session_id: sessionId,
      lesson_id: session.lesson_id,
      student_name: studentName,
      total_score: totalScore,
      max_possible_score: maxPossibleScore,
      completion_percentage: Math.round(completionRate)
    });

  if (gradesError && gradesError.code !== 'PGRST116') {
    console.error('[Analytics] Error updating grades table:', gradesError);
  }

  // Update grade_summary table if it exists
  try {
    // First, try to get teacher_id from the session
    const { data: sessionData } = await supabase
      .from('sessions')
      .select('teacher_id')
      .eq('id', sessionId)
      .single();

    const teacherId = sessionData?.teacher_id || '';

    const { error: summaryError } = await supabase
      .from('grade_summary')
      .upsert({
        session_id: sessionId,
        lesson_id: session.lesson_id,
        student_name: studentName,
        teacher_id: teacherId,
        total_score: totalScore,
        max_possible_score: maxPossibleScore,
        percentage: percentage,
        completed_activities: completedActivities,
        total_activities: totalActivities,
        completion_rate: completionRate,
        participation_rate: completionRate, // Using completion as participation for now
        time_spent_minutes: 0, // TODO: Calculate from submissions if needed
        last_activity_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      });

    if (summaryError && summaryError.code !== 'PGRST116' && summaryError.code !== '42P01') {
      console.warn('[Analytics] grade_summary update warning:', summaryError);
    }
  } catch (summaryErr) {
    console.log('[Analytics] grade_summary table not available, using grades table only');
  }

  console.log('[Analytics] Student grade recalculated:', {
    totalScore,
    maxPossibleScore,
    percentage: percentage.toFixed(2),
    completedActivities,
    totalActivities
  });
}

// ============================================================
// ANALYTICS REFRESH & SYNCHRONIZATION
// ============================================================

/**
 * Refresh analytics views - views are automatically updated
 * This exists for compatibility with older code
 */
export async function refreshAnalyticsViews(): Promise<void> {
  console.log('[Analytics] Refreshing analytics data');
  
  // Regular views automatically reflect the latest data, no RPC needed
  // This function exists for compatibility but doesn't need to do anything
  
  console.log('[Analytics] Analytics data is current');
}

/**
 * Sync analytics data for a specific session
 * Recalculates grade_summary for all students in the session
 */
export async function syncSessionAnalytics(sessionId: string): Promise<void> {
  console.log('[Analytics] Syncing session analytics:', sessionId);
  
  const { error } = await supabase.rpc('sync_session_analytics', {
    p_session_id: sessionId
  });

  if (error) {
    console.error('[Analytics] Error syncing session analytics:', error);
    throw error;
  }

  console.log('[Analytics] Session analytics synced successfully');
}

/**
 * Sync analytics data for a specific lesson
 * Recalculates grade_summary for all sessions and students in the lesson
 */
export async function syncLessonAnalytics(lessonId: string): Promise<void> {
  console.log('[Analytics] Syncing lesson analytics:', lessonId);
  
  const { error } = await supabase.rpc('sync_lesson_analytics', {
    p_lesson_id: lessonId
  });

  if (error) {
    console.error('[Analytics] Error syncing lesson analytics:', error);
    throw error;
  }

  console.log('[Analytics] Lesson analytics synced successfully');
}

/**
 * Sync all analytics data for a teacher
 * Recalculates grade_summary for all lessons, sessions, and students
 */
export async function syncTeacherAnalytics(teacherId: string): Promise<void> {
  console.log('[Analytics] Syncing teacher analytics:', teacherId);
  
  const { error } = await supabase.rpc('sync_teacher_analytics', {
    p_teacher_id: teacherId
  });

  if (error) {
    console.error('[Analytics] Error syncing teacher analytics:', error);
    throw error;
  }

  console.log('[Analytics] Teacher analytics synced successfully');
}

/**
 * Update session analytics data (legacy function for backward compatibility)
 */
export async function updateSessionAnalyticsData(sessionId: string): Promise<void> {
  console.log('[Analytics] Updating session analytics:', sessionId);
  
  // Use the new sync function
  await syncSessionAnalytics(sessionId);

  console.log('[Analytics] Session analytics updated');
}

// ============================================================
// EXPORT TO CSV
// ============================================================

export function exportToCSV(data: any[], filename: string): void {
  if (!data || data.length === 0) {
    console.warn('[Analytics] No data to export');
    return;
  }

  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(','),
    ...data.map(row => 
      headers.map(header => {
        const value = row[header];
        // Escape commas and quotes
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value ?? '';
      }).join(',')
    )
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  console.log('[Analytics] CSV exported:', filename);
}

// ============================================================
// GRADE RECALCULATION
// ============================================================

/**
 * Manually recalculate all grades and sync them to grade_summary
 * Useful for fixing data after a migration or bug fix
 */
export async function recalculateAllGradesAndSummaries(): Promise<number> {
  console.log('[Analytics] Recalculating all grades and summaries from database');
  
  try {
    const { data, error } = await supabase.rpc('recalculate_all_grades_and_summaries');
    
    if (error) {
      console.error('[Analytics] Error recalculating grades:', error);
      throw error;
    }
    
    const affectedRecords = data?.[0]?.affected_records || 0;
    console.log(`[Analytics] Successfully recalculated ${affectedRecords} grade records`);
    
    return affectedRecords;
  } catch (error) {
    console.error('[Analytics] Failed to recalculate grades:', error);
    throw error;
  }
}
